import React from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toBase64OrUrl } from '@/utils/pdfGenerator';
import { captureException } from '@/lib/monitoring';
import type { CollectionTermDocumentProps } from '@/components/Documents/Templates/CollectionTermDocument';

export interface GenerateAndAttachCollectionTermParams {
  /** event_id retornado por confirm_rental_collection_with_signature */
  eventId: string;
  companyId: string;
  rentalNumber: string;
  term: CollectionTermDocumentProps;
}

export interface CollectionTermResult {
  ok: boolean;
  /** Blob disponível sempre que a geração tiver sucesso, mesmo se upload/attach falharem */
  pdfBlob?: Blob;
  pdfPath?: string;
}

/**
 * Gera o PDF do Termo de Coleta, faz upload ao Storage (mesmo bucket/padrão
 * dos recibos de locação — ver receiptPdfStorage.ts) e anexa o path ao evento
 * de coleta via RPC attach_collection_term_pdf.
 *
 * IMPORTANTE: a coleta já está confirmada no banco quando esta função roda.
 * Falha aqui NUNCA desfaz a coleta — loga no Sentry e retorna { ok: false }
 * para a UI avisar sem bloquear.
 */
export async function generateAndAttachCollectionTerm(
  params: GenerateAndAttachCollectionTermParams,
): Promise<CollectionTermResult> {
  const { eventId, companyId, rentalNumber, term } = params;

  // ── 1. Gerar o PDF (import dinâmico — padrão generateReturnReceiptPDF) ──
  let pdfBlob: Blob;
  try {
    const logoBase64 = term.company.logo_url
      ? await toBase64OrUrl(term.company.logo_url)
      : undefined;
    const { pdf } = await import('@react-pdf/renderer');
    const { CollectionTermDocument } = await import(
      '@/components/Documents/Templates/CollectionTermDocument'
    );
    pdfBlob = await pdf(
      React.createElement(CollectionTermDocument, {
        ...term,
        company: { ...term.company, logo_url: logoBase64 },
      })
    ).toBlob();
  } catch (err) {
    captureException(err, { context: 'generateAndAttachCollectionTerm.generate', eventId });
    return { ok: false };
  }

  // ── 2. Upload ao Storage (bucket dos anexos de locação) ─────────────────
  // Padrão §I.93: TERMO_COLETA_LOC-xxxxx_YYYY-MM-DD_HHMMSS.pdf — legível,
  // ordenável e único na prática (D4 permite N coletas parciais por contrato;
  // o sufixo de hora impede que uma sobrescreva o termo assinado da outra).
  const safeRentalNumber = rentalNumber.replace(/[^a-zA-Z0-9-_]/g, '_');
  const timestamp = format(new Date(), "yyyy-MM-dd_HHmmss");
  const storagePath = `${companyId}/collection-terms/${rentalNumber}/TERMO_COLETA_${safeRentalNumber}_${timestamp}.pdf`;
  try {
    const { error: uploadError } = await supabase.storage
      .from('rental-attachments')
      .upload(storagePath, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: true,
      });
    if (uploadError) throw uploadError;
  } catch (err) {
    captureException(err, {
      context: 'generateAndAttachCollectionTerm.upload',
      eventId,
      storagePath,
    });
    return { ok: false, pdfBlob };
  }

  // ── 3. Anexar o path ao evento de coleta (metadata.pdf_path) ────────────
  try {
    const { error: attachError } = await (supabase.rpc as any)(
      'attach_collection_term_pdf',
      { p_event_id: eventId, p_pdf_path: storagePath }
    );
    if (attachError) throw attachError;
  } catch (err) {
    captureException(err, {
      context: 'generateAndAttachCollectionTerm.attach',
      eventId,
      storagePath,
    });
    return { ok: false, pdfBlob, pdfPath: storagePath };
  }

  return { ok: true, pdfBlob, pdfPath: storagePath };
}
